import { gql, useMutation } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { useAppState } from '../../state/AppState';
import { TAVPermission, useVideoController } from './VideoControlContext';

const NO_OP = (...args: any) => {};
const DEBUG_LOG = NO_OP;

const STOP_SHARING_SCREEN = gql`
  mutation StepDownAsMain($partyKey: String!, $participantId: String) {
    updateMainParticipantStream(input: { partyKey: $partyKey, participantId: $participantId }) {
      success
    }
  }
`;

const ShareScreen = () => {
  const { user, party } = useAppState();
  const { session, sessionConnected } = useVideoController();

  const [avPermission, setPermissionChanged] = useState<TAVPermission>('unknown');
  const [publisher, setPublisher] = useState<OT.Publisher | null>(null);

  const [onStopSharing, { loading }] = useMutation(STOP_SHARING_SCREEN);

  useEffect(() => {
    let cancel = false;
    let newPublisher: OT.Publisher | null;

    const initPub = async () => {
      const options: OT.PublisherProperties = {
        insertMode: 'append',
        mirror: false,
        frameRate: 30,
        width: '100%',
        height: '100%',
        name: `screenshare-${user!.uid}`,
        audioSource: false,
        // showControls: false,
        style: {
          audioLevelDisplayMode: 'off',
          buttonDisplayMode: 'off',
          nameDisplayMode: 'off',
          archiveStatusDisplayMode: 'off',
          backgroundImageURI: user?.photoURL,
        },
        videoSource: 'screen',
        maxResolution: { width: 1920, height: 1080 },
        // videoContentHint: 'detail', // update OpenTok to enable this
      };

      const screenPublisherElement = document.createElement('div');

      newPublisher = OT.initPublisher(screenPublisherElement, options, (err) => {
        if (err) {
          console.error('[ot] publisher initialization error: ', err);
          if (err.name === 'OT_USER_MEDIA_ACCESS_DENIED') {
            setPermissionChanged('denied');
          }
          return;
        }
        if (!cancel && newPublisher) {
          setPermissionChanged('allowed');
          setPublisher(newPublisher);
        }
      });

      newPublisher.on('mediaStopped', (event) => {
        onStopSharing({
          variables: {
            partyKey: party?.key,
            participantId: null,
          },
        }).catch((err) => {
          console.log(err);
        });
      });

      newPublisher.on('streamDestroyed', (event) => {
        if (
          event.stream?.connection.connectionId === newPublisher?.stream?.connection.connectionId
        ) {
          DEBUG_LOG('pub streamDestroyed', event);
        }
      });

      // Should happen when webcam permissions are revoked
      newPublisher.once('destroyed', (event) => {
        // DEBUG_LOG('pub destroyed', event);
        setPublisher(null);
        setPermissionChanged('denied');
      });

      newPublisher.on('accessDialogClosed', (event) => {
        // DEBUG_LOG('[ot] access dialog closed', event);
        // setPermissionChanged('allowed');
      });

      newPublisher.on('accessAllowed', () => {
        // DEBUG_LOG('[ot] access allowed');
        setPermissionChanged('allowed');
        // newPublisher?.publishAudio(enableAV);
        // newPublisher?.publishVideo(enableAV);
      });

      newPublisher.on('mediaStopped', (event) => {
        // DEBUG_LOG('[ot] media stopped', event);
      });

      newPublisher.on('accessDenied', () => {
        console.error('[ot] access denied');
        // Removing for now as this is firing every time we poll
        // LOG_EVENT('webcam_access_denied', { party_key: partyKey });
        setPermissionChanged('denied');
      });

      newPublisher.on('streamCreated', (event) => {
        // DEBUG_LOG('[ot] publisher mounted, setting tracks');
        setPermissionChanged('allowed');
        event.target.element?.getElementsByTagName('video')?.[0]?.play();
      });
    };

    initPub();

    return () => {
      cancel = true;
      setPublisher(null);
      if (newPublisher) {
        // DEBUG_LOG('unmounting publisher');
        newPublisher.destroy();
      }
    };
  }, [setPublisher, user, onStopSharing, party?.key]);

  useEffect(() => {
    if (!publisher || !session || !sessionConnected) {
      return;
    }

    if (publisher.session === session) {
      return;
    }

    let cancel = false;
    let retryTimeout: NodeJS.Timeout | null = null;

    // DEBUG_LOG('[ot] about to publish publisher: ', publisher?.id, session?.sessionId);

    const publishCallback = (err?: OT.OTError) => {
      if (cancel) return;
      // DEBUG_LOG('[ot] published ', publisher.id);
      if (err) {
        if (
          err.message.startsWith(
            'Session.publish :: SetRemoteDescription: Publisher PeerConnection with connection symphony.mantis013-pdx.tokbox.com failed: Error while setting RemoteDescription InvalidAccessError: Offer and answer have different number of m-lines'
          )
        ) {
          // DEBUG_LOG('[ot] server/user mismatch, retrying');
          retryTimeout = setTimeout(() => {
            if (cancel) return;
            session.publish(publisher, publishCallback);
          }, 2000);
        }
        console.error('[ot] published error: ', err);
      }
    };

    session.publish(publisher, publishCallback);

    return () => {
      clearTimeout(retryTimeout as NodeJS.Timeout);
      cancel = true;

      // if we don't destroy, next time we publish we get local black screen
      if (publisher) {
        publisher.destroy();
      }
    };
  }, [publisher, session, sessionConnected]);

  return <></>;
};

export default ShareScreen;
